<template>
  <table class="table table-bordered table-calendar">
    <thead>
      <tr>
        <th scope="col" class="text-muted">日</th>
        <th scope="col" class="text-muted">一</th>
        <th scope="col" class="text-muted">二</th>
        <th scope="col" class="text-muted">三</th>
        <th scope="col" class="text-muted">四</th>
        <th scope="col" class="text-muted">五</th>
        <th scope="col" class="text-muted">六</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(_, row) in numberOfWeeksInMonth" :key="row">
        <td
          v-for="(__, column) in 7" :key="column"
          :class="{
            'date': getDateOfCell(row, column),
            'highlight': parsedSelectedDate === getDateOfCell(row, column),
          }"
          :data-set-var="duration = getDurationOfCell(row, column)"
          @click="selectDate(row, column)"
        >
          <span class="text-muted">{{ getDateOfCell(row, column) || '' }}</span>
          <!-- eslint-disable max-len -->
          <span v-if="duration !== null" class="total-duration" :class="{'text-lighter': duration === 0}">
            {{ Math.trunc(duration / 60).toString() }} : {{ (duration % 60).toString().padStart(2, '0') }}
          </span>
          <!-- eslint-enable max-len -->
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
export default {
  name: 'TimeEntrySummary',
  props: {
    year: Number, // four digit year, e.g. 2022
    month: Number, // Jan is 1, Feb is 2, etc.
    totalDuration: Object, // { 'YYYY/MM/DD': INTEGER_MINUTES }
    selectedDate: String, // null or 'YYYY/MM/DD'
  },
  computed: {
    numberOfDaysInMonth() {
      return new Date(this.year, this.month, 0).getDate();
    },
    dayOffsetInMonth() {
      return new Date(this.year, this.month - 1, 1).getDay();
    },
    numberOfWeeksInMonth() {
      return Math.ceil((this.dayOffsetInMonth + this.numberOfDaysInMonth) / 7);
    },
    parsedSelectedDate() {
      if (this.selectedDate === null) return -1;
      return parseInt(this.selectedDate.substring(8, 10), 10);
    },
  },
  methods: {
    /**
     * 月曆上，位於第幾列、第幾欄的格子，應該填入什麼日期
     * （如果不應填入任何日期，回傳 null）
     * @param {number} row 0-indexed row number
     * @param {number} column 0-indexed column number
     * @returns {?number} day of month, the first day is 1
     */
    getDateOfCell(row, column) {
      const cellNumber = row * 7 + column + 1; // 1-indexed
      const date = cellNumber - this.dayOffsetInMonth; // 1-indexed
      // eslint-disable-next-line yoda
      return (1 <= date && date <= this.numberOfDaysInMonth) ? date : null;
    },
    /**
     * 月曆上，位於第幾列、第幾欄的日期，總時數為多少
     * （如果那個格子沒有日期，回傳 null）
     * @param {number} row 0-indexed row number
     * @param {number} column 0-indexed column number
     * @returns {?number} total duration of the date (in minutes)
     */
    getDurationOfCell(row, column) {
      const date = this.getDateOfCell(row, column);
      if (date === null) return null;
      // eslint-disable-next-line prefer-template
      const fullDate = this.year.toString().padStart(4, '0') + '/'
                     + this.month.toString().padStart(2, '0') + '/'
                     + date.toString().padStart(2, '0');
      if (!Object.prototype.hasOwnProperty.call(this.totalDuration, fullDate)) {
        return 0;
      }
      return this.totalDuration[fullDate];
    },
    selectDate(row, column) {
      const date = this.getDateOfCell(row, column);
      if (date === null) return;
      // eslint-disable-next-line prefer-template
      const fullDate = this.year.toString().padStart(4, '0') + '/'
                     + this.month.toString().padStart(2, '0') + '/'
                     + date.toString().padStart(2, '0');
      if (fullDate === this.selectedDate) {
        this.$emit('update:selectedDate', null);
      } else {
        this.$emit('update:selectedDate', fullDate);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "sass:math";
@use "sass:color";
@import "~bootstrap/scss/bootstrap-grid";
.table.table-calendar {
  // overwrite Bootstrap CSS
  border-collapse: separate;
  border-spacing: 0;
  border-color: #e6e6e7;
  margin-bottom: 0.25rem;
  &.table-bordered {
    > :not(caption) > * {
      // overwrite Bootstrap CSS
      border-width: 0;
    }
    > :not(caption) > * > * {
      // overwrite Bootstrap CSS
      border-width: 0 1px 1px 0;
    }
    td:first-of-type {
      border-left-width: 1px;
    }
    th {
      border-top-width: 1px;
    }
    th:first-of-type {
      border-left-width: 1px;
    }
    $table-border-radius: 10px;
    th:first-of-type {
      border-top-left-radius: $table-border-radius;
    }
    th:last-of-type {
      border-top-right-radius: $table-border-radius;
    }
    tr:last-of-type td:first-of-type {
      border-bottom-left-radius: $table-border-radius;
    }
    tr:last-of-type td:last-of-type {
      border-bottom-right-radius: $table-border-radius;
    }
  }
  td:not(.date),
  th {
    background-color: #e6e6e7;
  }
  th {
    text-align: center;
    width: math.div(100%, 7);
  }
  td.date {
    padding-top: 0.25rem;
    cursor: pointer;
  }
  span.total-duration {
    display: block;
    font-size: 1.15rem;
    font-weight: bold;
    text-align: center;
    padding-top: 0.15rem;
    padding-bottom: 0.3rem;
    line-height: 1.15;
    @include media-breakpoint-up(sm) {
      padding-bottom: 0.7rem;
      line-height: var(--bs-body-line-height);
    }
  }
}
.text-lighter {
  color: #e6e6e7 !important;
}
.highlight {
  background-color: #0069b71f;
  .text-muted {
    color: color.scale(#0069b7, $saturation: -48%) !important;
  }
  .total-duration {
    color: color.scale(#0069b7, $blackness: 36%) !important;
  }
  .text-lighter {
    color: #0069b72c !important;
  }
}
</style>
